/**
 * Three-ring meter renderer — public API + factory.
 *
 * Each phase has a monochrome 3-shade ladder (light → base → dark)
 * mapped across the outer → mid → inner rings. Only one phase's rings
 * paint at a time; inactive rings render as phantom tracks at low
 * alpha. A soft shadow glow behind the painted arcs uses the phase's
 * base shade at low alpha with a fraction of the reserved blur
 * margin.
 *
 * The 3-shade ladder is DERIVED, not configured. Each phase's middle
 * shade is `METRIC_DEFINITIONS[phase].color` — the JS-side mirror of
 * the `--phase-{phase}` canon in `styles/tokens.css`. The light shade
 * is the base blended toward white; the dark shade toward black.
 * There is no CSS ladder token set any more — the canvas rings can
 * never drift from a phase's chart hue or its WCAG label companion
 * because all three forms resolve from the one canon.
 *
 * Module layout (AH-W5-a.2 split, FA-1 §3.3):
 *   • `rings.ts` (this file) — public draw* API + `createRings`
 *     factory + `RingSet` / `RingSpec` / `RingKey` / `PhaseKey` types.
 *     Re-exports `crossfadeLadder` + the `LADDER_LIGHTEN` /
 *     `LADDER_DARKEN` constants so existing consumers + tests keep
 *     reaching `./rings` unchanged.
 *   • `rings-color.ts` — pure color math: hex parse, mixHex,
 *     phaseLadder, crossfadeLadder, LADDER_* constants. No canvas
 *     dependency.
 *   • `rings-paint.ts` — canvas-2D primitives: strokeArc, sweepGradient,
 *     paintPhantomTracks, paintActivePhase, asPhaseKey.
 */

import { Canvas } from "../core";
import type { MeterConfig } from "./config";
import { METRIC_DEFINITIONS } from "@src/types/speedtest.ts";

import {
    type PhaseKey,
    hexToRgba,
    phaseLadder,
    crossfadeLadder,
    LADDER_LIGHTEN,
    LADDER_DARKEN,
} from "./rings-color";
import {
    asPhaseKey,
    paintActivePhase,
    paintPhantomTracks,
    strokeArc,
} from "./rings-paint";

// ── Types ─────────────────────────────────────────────────────────────

export type { PhaseKey } from "./rings-color";

export type RingKey = "outer" | "mid" | "inner";

export interface RingSpec {
    key: RingKey;
    radius: number;
    lineWidth: number;
    colorIdx: 0 | 1 | 2;
}

export interface RingSet {
    cfg: MeterConfig;
    specs: RingSpec[];
    stops: Record<PhaseKey, [string, string, string]>;
    bgColor: string;
}

// ── Re-exports — preserve consumer + test import paths ────────────────
//
// Consumers (paintMeter, useMeterGeometry, useMeterCompletion) +
// `rings.test.ts` historically reached `./rings` for both the public
// draw* API AND the color helpers. The split moves the color helpers
// to `rings-color.ts`; the re-exports below keep the existing import
// shape working without churning every caller.

export { crossfadeLadder, LADDER_LIGHTEN, LADDER_DARKEN };

// ── Factory ───────────────────────────────────────────────────────────

export function createRings(
    cfg: MeterConfig,
    bgColor: string,
    _canvas: HTMLCanvasElement,
    isDark = false,
): RingSet {
    const specs: RingSpec[] = [
        {
            key: "outer",
            radius: cfg.outerRadius,
            lineWidth: cfg.outerLineWidth,
            colorIdx: 0,
        },
        {
            key: "mid",
            radius: cfg.midRadius,
            lineWidth: cfg.midLineWidth,
            colorIdx: 1,
        },
        {
            key: "inner",
            radius: cfg.innerRadius,
            lineWidth: cfg.innerLineWidth,
            colorIdx: 2,
        },
    ];
    // DESIGN-INTAKE-2 line-54 — the ladder's dark rung is scheme-aware
    // (`phaseLadder(base, isDark)`): in dark the inner rung lightens toward
    // white rather than darkening toward black, so the active phase hue
    // reads at stat-card saturation against the dark ground instead of
    // washing to grey. The geometry rebuild that runs on a dark toggle
    // (`installDarkModeSync` → recompute) re-derives the stops for the live
    // scheme, so the ladder tracks the theme without a remount.
    const stops: Record<PhaseKey, [string, string, string]> = {
        ping: phaseLadder(METRIC_DEFINITIONS.ping.color, isDark),
        download: phaseLadder(METRIC_DEFINITIONS.download.color, isDark),
        upload: phaseLadder(METRIC_DEFINITIONS.upload.color, isDark),
    };
    return { cfg, specs, stops, bgColor };
}

// ── Public draw API ───────────────────────────────────────────────────

export function drawRings(
    canvas: Canvas,
    rings: RingSet,
    stateName: string | undefined,
    t: number,
): void {
    if (!rings.cfg.valid) return;

    const ctx = canvas.ctx;
    paintPhantomTracks(ctx, rings);

    const activePhase = asPhaseKey(stateName);
    if (!activePhase || t <= 0) return;

    paintActivePhase(ctx, rings, activePhase, t);
}

/**
 * W5-C4 — the meter milestone bloom.
 *
 * A one-shot ring-glow that fires when the needle first SETTLES within an
 * active phase (`SmoothProgress.settled` flips true — the metric has
 * converged on its measured value). It is the canvas-side member of the
 * "affirmation" motion family: the dial's per-phase "got it" beat,
 * rhyming with the timeline's completion tick.
 *
 * `intensity ∈ [0, 1]` is the bloom envelope — a keyframes.js
 * `NumericAnimation` over an ease-out curve, owned by `useMeterMilestone`.
 * The bloom paints AFTER the active arcs: a single low-alpha pass of the
 * phase's mid shade with a wide shadow blur, so the active ring briefly
 * suffuses with light. At `intensity === 0` it is a no-op — only the
 * ≈400ms bloom window pays the cost.
 */
export function drawMilestoneBloom(
    canvas: Canvas,
    rings: RingSet,
    stateName: string | undefined,
    intensity: number,
): void {
    if (!rings.cfg.valid || intensity <= 0) return;
    const phase = asPhaseKey(stateName);
    if (!phase) return;

    const ctx = canvas.ctx;
    const { originX, originY, startAngle, endAngle, blurMargin } = rings.cfg;
    const ladder = rings.stops[phase];
    // The bloom rides the mid ring — the phase's protagonist arc. Alpha
    // and blur both scale with the envelope so the glow swells then fades.
    const mid = rings.specs.find((s) => s.key === "mid") ?? rings.specs[0];
    const glow = hexToRgba(ladder[1], 0.42 * intensity);
    strokeArc(
        ctx,
        originX,
        originY,
        mid.radius,
        startAngle,
        endAngle,
        ladder[1],
        mid.lineWidth,
        1,
        {
            shadowColor: glow,
            shadowBlur: Math.max(1, blurMargin * (0.6 + 0.9 * intensity)),
            alpha: 0.5 * intensity,
        },
    );
}

/**
 * W5-L2 — paint the active ring stack with its hue cross-faded across a
 * phase boundary.
 *
 * `fromPhase` → `toPhase` over the handoff weight `blend ∈ [0, 1]`: the
 * three rings interpolate their ladder rungs so the dial colour glides
 * instead of hard-cutting. `fill` is the needle-progress fill the rings
 * stroke to (same semantics as `drawRings`'s `t`).
 *
 * Three boundary shapes, one clock:
 *
 *   • INTERIOR (`ping ↔ download ↔ upload`) — both endpoints present:
 *     a genuine hue cross-fade between the two ladders.
 *   • ARRIVAL (`undefined → ping`) — only `toPhase`: the rings fade UP
 *     from the phantom-track substrate into the opening phase, the
 *     `alphaScale` ramping `blend → 1`.
 *   • RETIREMENT (`upload → undefined`) — only `fromPhase`: the rings
 *     fade DOWN to the phantom substrate, `alphaScale` ramping `1 − blend`.
 *
 * Before W3-AG the one-sided edges hard-cut (a solo full-alpha paint)
 * while the CSS `--phase-color` cross-faded — "the screen had multiple
 * opinions" (AG-A4-EDGE-ASYMMETRY). Now every shape rides the same
 * `usePhaseTransition` `ManualTimeline`, so the rings and the CSS
 * `--phase-color` finish on one instant.
 */
export function drawRingsCrossfade(
    canvas: Canvas,
    rings: RingSet,
    fromPhase: string | undefined,
    toPhase: string | undefined,
    blend: number,
    fill: number,
): void {
    if (!rings.cfg.valid) return;

    const ctx = canvas.ctx;
    paintPhantomTracks(ctx, rings);

    if (fill <= 0) return;

    const from = asPhaseKey(fromPhase);
    const to = asPhaseKey(toPhase);
    const w = Math.min(1, Math.max(0, blend));

    // INTERIOR — both endpoints present → genuine hue cross-fade. The
    // painted phase identity is the incoming phase (`to`); only the hue
    // interpolates, full opacity throughout.
    if (from && to) {
        const ladder = crossfadeLadder(rings.stops[from], rings.stops[to], w);
        paintActivePhase(ctx, rings, to, fill, ladder);
        return;
    }

    // ARRIVAL — only the incoming phase: the rings fade up from the
    // phantom substrate, alpha ramping with the clock.
    if (to) {
        paintActivePhase(ctx, rings, to, fill, undefined, w);
        return;
    }

    // RETIREMENT — only the outgoing phase: the rings fade down toward
    // the phantom substrate, alpha ramping out with the clock.
    if (from) {
        paintActivePhase(ctx, rings, from, fill, undefined, 1 - w);
    }
}

/**
 * Paint multiple phases at the given fill `t`, each phase's ring ladder
 * stacked into the same canvas. Phantom tracks paint exactly once
 * (3 strokes); each named phase contributes 6 active strokes
 * (3 rings × 2 passes — glow + crisp). Used by the post-complete hold
 * to render the full rainbow rather than a single active phase.
 */
export function drawAllRings(
    canvas: Canvas,
    rings: RingSet,
    stateNames: string[],
    t: number,
): void {
    if (!rings.cfg.valid) return;

    const ctx = canvas.ctx;
    paintPhantomTracks(ctx, rings);

    if (t <= 0) return;

    for (const name of stateNames) {
        const phase = asPhaseKey(name);
        if (phase) paintActivePhase(ctx, rings, phase, t);
    }
}
